// Hazard forecast (§10.1, §10.2 EVADE): one per room, refreshed lazily. Two sources:
//  - `MapDef.danger(w, x, y, aheadTicks)` sampled on a coarse grid every few ticks (the map's own
//    prediction from the same pure functions the sim uses), read back bilinearly;
//  - the bot's own reading of `MapDef.hazardPose` and the kinematic solids: when a sweeper arm or
//    a giant pinball reaches a point, which bubbles wobble and which candles flicker. That drives
//    jump timing and works even where a map's `danger` is coarse.
import { TUNE } from '../constants';
import { MapId } from '../ids';
import { MAPS } from '../maps/index';
import { SolidKind, SolidTag, createHazardPose, createSolid, type Solid } from '../maps/mapTypes';
import type { World } from '../types';
import { BOT_TUNE } from './tuning';
import { datan2 } from '../sim/dmath';

const TAU = Math.PI * 2;
const NEVER = 1e9;
/** Pinball forecast: samples every BALL_STEP ticks, BALL_STEPS ahead (60 ticks). */
const BALL_STEP = 2;
const BALL_STEPS = 30;

function wrap2pi(a: number): number {
  let r = a % TAU;
  if (r < 0) r += TAU;
  return r;
}

export class HazardForecast {
  private readonly H = BOT_TUNE.hazard;
  // Coarse danger grid.
  private x0 = 0;
  private y0 = 0;
  private nx = 0;
  private ny = 0;
  private grid = new Float64Array(0);
  private gridTick = -NEVER;
  private mapId = -1;
  /** False until the map's danger() has returned something non-zero (skips grid lookups). */
  private anyDanger = false;
  // Poses now and one tick ahead.
  private readonly pose = createHazardPose();
  private readonly pose1 = createHazardPose();
  private poseTick = -NEVER;
  private readonly solids: Solid[] = [];
  private solidCount = 0;
  // Sweeper geometry and angular speed (rad/tick, signed).
  armCount = 0;
  private armIn = 0;
  private armOut = 0;
  private armR = 0;
  private readonly armAngle = new Float64Array(4);
  private armOmega = 0;
  // Pinballs: where each will be over the next BALL_STEPS × BALL_STEP ticks (a pure function of the
  // tick, read from the map's pose, so the loop's corners are exact).
  ballCount = 0;
  private ballR = 0;
  private readonly ballX = new Float64Array(4);
  private readonly ballY = new Float64Array(4);
  private readonly ballVX = new Float64Array(4);
  private readonly ballVY = new Float64Array(4);
  private readonly ballOn = new Uint8Array(4);
  private readonly futX = new Float64Array(4 * (BALL_STEPS + 1));
  private readonly futY = new Float64Array(4 * (BALL_STEPS + 1));
  private readonly futOn = new Uint8Array(4 * (BALL_STEPS + 1));
  private readonly futPose = createHazardPose();
  // Candles (cake): positions from the candle solids, flicker/flame from the pose.
  private candleCount = 0;
  private readonly candleX = new Float64Array(8);
  private readonly candleY = new Float64Array(8);

  reset(): void {
    this.gridTick = -NEVER;
    this.poseTick = -NEVER;
    this.mapId = -1;
    this.anyDanger = false;
  }

  /** Brings the forecast up to date for `w.tick` (cheap when already current). */
  update(w: World): void {
    const map = MAPS[w.cfg.mapId];
    if (this.mapId !== map.id) {
      this.mapId = map.id;
      const cell = this.H.dangerCell;
      this.x0 = map.bounds.minX - cell;
      this.y0 = map.bounds.minY - cell;
      this.nx = Math.floor((map.bounds.maxX + cell - this.x0) / cell) + 1;
      this.ny = Math.floor((map.bounds.maxY + cell - this.y0) / cell) + 1;
      this.grid = new Float64Array(this.nx * this.ny);
      this.gridTick = -NEVER;
      this.anyDanger = false;
    }
    if (w.tick - this.gridTick >= this.H.dangerRefreshTicks || w.tick < this.gridTick) this.refreshGrid(w);
    if (w.tick !== this.poseTick) this.refreshPose(w);
  }

  private refreshGrid(w: World): void {
    const map = MAPS[w.cfg.mapId];
    const cell = this.H.dangerCell;
    const ahead = TUNE.bot.dangerAheadTicks;
    let any = false;
    for (let j = 0; j < this.ny; j++) {
      for (let i = 0; i < this.nx; i++) {
        const v = map.danger(w, this.x0 + i * cell, this.y0 + j * cell, ahead);
        const c = v > 0 ? (v > 1 ? 1 : v) : 0;
        this.grid[j * this.nx + i] = c;
        if (c > 0) any = true;
      }
    }
    this.anyDanger = any;
    this.gridTick = w.tick;
  }

  private refreshPose(w: World): void {
    const map = MAPS[w.cfg.mapId];
    this.poseTick = w.tick;
    while (this.solids.length < BOT_TUNE.maxSolids) this.solids.push(createSolid());
    this.solidCount = map.solids(w, this.solids);
    map.hazardPose(w, w.tick, this.pose);
    map.hazardPose(w, w.tick + 1, this.pose1);
    this.readArms();
    this.readBalls(w);
    this.readCandles(w);
  }

  private readArms(): void {
    this.armCount = 0;
    let rin = NEVER;
    let rout = 0;
    let r = 0;
    for (let s = 0; s < this.solidCount; s++) {
      const sol = this.solids[s];
      if (sol.tag !== SolidTag.SWEEPER || sol.kind !== SolidKind.CAPSULE) continue;
      const ca = Math.hypot(sol.ax - sol.cx, sol.ay - sol.cy);
      const cb = Math.hypot(sol.bx - sol.cx, sol.by - sol.cy);
      rin = Math.min(rin, ca, cb);
      rout = Math.max(rout, ca, cb);
      r = sol.r;
    }
    const n = Math.min(this.pose.armCount, this.armAngle.length);
    if (rout === 0 || n === 0) return;
    this.armCount = n;
    this.armIn = rin;
    this.armOut = rout;
    this.armR = r;
    for (let i = 0; i < n; i++) this.armAngle[i] = this.pose.armAngles[i];
    let d = this.pose1.armAngles[0] - this.pose.armAngles[0];
    d = wrap2pi(d + Math.PI) - Math.PI;
    this.armOmega = d;
  }

  private readBalls(w: World): void {
    this.ballCount = 0;
    let r = 0;
    for (let s = 0; s < this.solidCount; s++) {
      const sol = this.solids[s];
      if (sol.tag === SolidTag.PINBALL) r = Math.max(r, sol.r);
    }
    if (r === 0) return;
    this.ballR = r;
    const n = Math.min(this.pose.balls.length, this.ballX.length);
    for (let i = 0; i < n; i++) {
      const b = this.pose.balls[i];
      const b1 = this.pose1.balls[i];
      this.ballOn[i] = b.active ? 1 : 0;
      this.ballX[i] = b.x;
      this.ballY[i] = b.y;
      this.ballVX[i] = b1.x - b.x;
      this.ballVY[i] = b1.y - b.y;
    }
    this.ballCount = n;
    const map = MAPS[w.cfg.mapId];
    const K = BALL_STEPS + 1;
    for (let k = 0; k < K; k++) {
      const p = k === 0 ? this.pose : map.hazardPose(w, w.tick + k * BALL_STEP, this.futPose);
      for (let i = 0; i < n; i++) {
        this.futX[i * K + k] = p.balls[i].x;
        this.futY[i * K + k] = p.balls[i].y;
        this.futOn[i * K + k] = p.balls[i].active ? 1 : 0;
      }
    }
  }

  private readCandles(w: World): void {
    this.candleCount = 0;
    if (w.cfg.mapId !== MapId.CAKE) return;
    for (let s = 0; s < this.solidCount && this.candleCount < this.candleX.length; s++) {
      const sol = this.solids[s];
      if (sol.kind !== SolidKind.CIRCLE || sol.tag !== SolidTag.NONE) continue;
      const i = sol.index;
      if (i < 0 || i >= this.candleX.length) continue;
      this.candleX[i] = sol.ax;
      this.candleY[i] = sol.ay;
      this.candleCount = Math.max(this.candleCount, i + 1);
    }
  }

  /** The map's danger at (x, y) over the next `dangerAheadTicks`, from the coarse grid. */
  mapDanger(x: number, y: number): number {
    if (!this.anyDanger || this.nx === 0) return 0;
    const cell = this.H.dangerCell;
    let fx = (x - this.x0) / cell;
    let fy = (y - this.y0) / cell;
    const mx = this.nx - 1.000001;
    const my = this.ny - 1.000001;
    fx = fx < 0 ? 0 : fx > mx ? mx : fx;
    fy = fy < 0 ? 0 : fy > my ? my : fy;
    const i = Math.floor(fx);
    const j = Math.floor(fy);
    const tx = fx - i;
    const ty = fy - j;
    const k = j * this.nx + i;
    const g = this.grid;
    const a = g[k] + (g[k + 1] - g[k]) * tx;
    const b = g[k + this.nx] + (g[k + this.nx + 1] - g[k + this.nx]) * tx;
    return a + (b - a) * ty;
  }

  /** Ticks until a sweeper arm reaches a grounded body of radius `r` at (x, y); NEVER if none will. */
  sweeperTicks(x: number, y: number, r: number): number {
    if (this.armCount === 0 || this.armOmega === 0) return NEVER;
    const rho = Math.hypot(x, y);
    const reach = this.armR + r;
    if (rho < this.armIn - reach || rho > this.armOut + reach || rho < 1e-6) return NEVER;
    // asin(reach / rho) without asin (§18.4 keeps to sqrt and atan2).
    const half = reach >= rho ? Math.PI / 2 : datan2(reach, Math.sqrt(rho * rho - reach * reach));
    const phi = datan2(y, x);
    const dir = this.armOmega > 0 ? 1 : -1;
    const speed = Math.abs(this.armOmega);
    let best = NEVER;
    for (let i = 0; i < this.armCount; i++) {
      const ahead = wrap2pi((phi - this.armAngle[i]) * dir);
      // Already overlapping (the arm is on us, or has just passed within its half-width).
      if (ahead <= half || ahead >= TAU - half) return 0;
      const t = (ahead - half) / speed;
      if (t < best) best = t;
    }
    return best;
  }

  /**
   * Contact window of the sweeper arms with a grounded body of radius `r` at (x, y) moving at
   * (vx, vy) m/s: `t0` ticks until an arm's leading edge touches it (0 if it already does) and `t1`
   * until the trailing edge clears it, with the body's own angular motion about the hub included
   * (running against an arm shortens the crossing). Returns false if no arm is closing in.
   */
  sweeperWindow(x: number, y: number, r: number, vx: number, vy: number, out: { t0: number; t1: number; dir: number }): boolean {
    if (this.armCount === 0 || this.armOmega === 0) return false;
    const rho = Math.hypot(x, y);
    const reach = this.armR + r;
    if (rho < this.armIn - reach || rho > this.armOut + reach || rho < 1e-6) return false;
    const half = reach >= rho ? Math.PI / 2 : datan2(reach, Math.sqrt(rho * rho - reach * reach));
    const phi = datan2(y, x);
    const dir = this.armOmega > 0 ? 1 : -1;
    const bodyOmega = (x * vy - y * vx) / (rho * rho) / 60;
    // Walking with an arm only delays it: never let the body's own motion hide it completely.
    const rel = Math.max(Math.abs(this.armOmega) * this.H.armMinCloseFrac, Math.abs(this.armOmega) - dir * bodyOmega);
    let best0 = NEVER;
    let best1 = NEVER;
    for (let i = 0; i < this.armCount; i++) {
      const a = wrap2pi((phi - this.armAngle[i]) * dir);
      let t0: number;
      let t1: number;
      if (a <= half) {
        t0 = 0;
        t1 = (a + half) / rel;
      } else if (a >= TAU - half) {
        t0 = 0;
        t1 = (a + half - TAU) / rel;
      } else {
        t0 = (a - half) / rel;
        t1 = (a + half) / rel;
      }
      if (t0 < best0) {
        best0 = t0;
        best1 = t1;
      }
    }
    out.t0 = best0;
    out.t1 = best1;
    out.dir = dir;
    return best0 < NEVER;
  }

  /** Arm sweep danger at (x, y): 1 when an arm arrives within `soonTicks`, fading to 0 at twice that. */
  sweeperDanger(x: number, y: number, r: number, soonTicks: number): number {
    const t = this.sweeperTicks(x, y, r);
    if (t >= NEVER) return 0;
    if (t <= soonTicks) return 1;
    return Math.max(0, 1 - (t - soonTicks) / soonTicks);
  }

  /**
   * Ticks until a giant pinball touches a body of radius `r` standing at (x, y) (assuming the ball
   * keeps its current velocity); NEVER if not within `horizon` ticks.
   */
  ballTicks(x: number, y: number, r: number, horizon: number): number {
    let best = NEVER;
    const K = BALL_STEPS + 1;
    const reach = this.ballR + r + this.H.ballMargin;
    const steps = Math.min(BALL_STEPS, Math.ceil(horizon / BALL_STEP));
    for (let i = 0; i < this.ballCount; i++) {
      for (let k = 0; k <= steps && k * BALL_STEP < best; k++) {
        const j = i * K + k;
        if (this.futOn[j] === 0) continue;
        if (Math.hypot(this.futX[j] - x, this.futY[j] - y) < reach) {
          best = Math.min(best, k * BALL_STEP);
          break;
        }
      }
    }
    return best <= horizon ? best : NEVER;
  }

  /** Index of the ball that would hit (x, y) first, or −1 (pair with `ballTicks`). */
  ballVelocity(x: number, y: number, r: number, horizon: number, out: { x: number; y: number; bx: number; by: number }): boolean {
    const K = BALL_STEPS + 1;
    const reach = this.ballR + r + this.H.ballMargin;
    const steps = Math.min(BALL_STEPS, Math.ceil(horizon / BALL_STEP));
    for (let k = 0; k <= steps; k++) {
      for (let i = 0; i < this.ballCount; i++) {
        const j = i * K + k;
        if (this.futOn[j] === 0 || Math.hypot(this.futX[j] - x, this.futY[j] - y) >= reach) continue;
        // The ball's motion where it reaches us (per tick), and where it is then.
        const j1 = k < BALL_STEPS ? j + 1 : j;
        const j0 = k < BALL_STEPS ? j : j - 1;
        out.x = (this.futX[j1] - this.futX[j0]) / BALL_STEP;
        out.y = (this.futY[j1] - this.futY[j0]) / BALL_STEP;
        out.bx = this.futX[j];
        out.by = this.futY[j];
        return true;
      }
    }
    return false;
  }

  /** Danger from soap bubbles about to pop (wobbling) near (x, y). */
  bubbleDanger(x: number, y: number): number {
    const bubbles = this.pose.bubbles;
    const reach = this.H.bubblePopRadius + this.H.bubbleMargin;
    let worst = 0;
    for (let i = 0; i < bubbles.length; i++) {
      const b = bubbles[i];
      if (b.scale <= 0 || b.wobble <= 0) continue;
      const d = Math.hypot(b.x - x, b.y - y);
      if (d < reach) worst = Math.max(worst, Math.min(1, b.wobble + 0.4) * (1 - d / reach * 0.5));
    }
    return worst;
  }

  /** True if a wobbling bubble's pop will reach (x, y). */
  bubbleSoon(x: number, y: number): boolean {
    const bubbles = this.pose.bubbles;
    for (let i = 0; i < bubbles.length; i++) {
      const b = bubbles[i];
      if (b.scale > 0 && b.wobble > this.H.bubbleJumpWobble && Math.hypot(b.x - x, b.y - y) < this.H.bubblePopRadius) return true;
    }
    return false;
  }

  /** Danger from candles that flicker (about to puff) or burn near (x, y). */
  candleDanger(x: number, y: number): number {
    if (this.candleCount === 0) return 0;
    const reach = this.H.flameRadius + this.H.flameMargin;
    let worst = 0;
    for (let i = 0; i < this.candleCount && i < this.pose.candleFlicker.length; i++) {
      const heat = Math.max(this.pose.candleFlicker[i], this.pose.candleFlame[i]);
      if (heat <= 0) continue;
      const d = Math.hypot(this.candleX[i] - x, this.candleY[i] - y);
      if (d < reach) worst = Math.max(worst, Math.min(1, heat + 0.3));
    }
    return worst;
  }

  /**
   * Combined danger at (x, y) for a grounded body of radius `r`: the map's own forecast, plus
   * pinballs about to roll through, wobbling bubbles, flickering candles and, unless the body can
   * hop it, a sweeper arm arriving.
   */
  danger(x: number, y: number, r: number, canJumpArms: boolean): number {
    // Spin Cycle's own forecast counts every arm pass, which a bean on its feet simply hops; the
    // arms and bubbles are read from the pose below instead.
    // Crumble Cake's forecast marks every cracking tile, the very tile a bean just stepped on; the
    // safety field already plans around tiles about to drop, and the candles are read below.
    let d = this.mapId === MapId.SPIN || this.mapId === MapId.CAKE ? 0 : this.mapDanger(x, y);
    if (this.ballCount > 0) {
      const t = this.ballTicks(x, y, r, this.H.ballHorizonTicks);
      if (t < NEVER) d = Math.max(d, 1 - t / (this.H.ballHorizonTicks * 1.25));
    }
    if (this.armCount > 0 && !canJumpArms) d = Math.max(d, this.sweeperDanger(x, y, r, this.H.armSoonTicks));
    if (this.candleCount > 0) d = Math.max(d, this.candleDanger(x, y));
    d = Math.max(d, this.bubbleDanger(x, y));
    return d > 1 ? 1 : d;
  }

}
